/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button } from 'reactstrap';
import { useAppSelector } from '../../reducers';
import { generateUrl } from '../../routes/util';
import Dashboard, { DashboardSection } from '../dashboard';
import Loader from '../loader';
import CustomReportManage from './CustomReportManage';
import CustomReportManageSubsidiary from './CustomReportManageSubsidiary';
import { CustomReportRoutes } from './CustomReportsRoot';
import CalculatedReportImage from '../../images/custom-reports/custom-report-calculated.png';
import SubsidiaryReportImage from '../../images/custom-reports/custom-report-subsidiary.png';
import DateReportImage from '../../images/custom-reports/custom-report-date.png';
import G17Client from '../../services/G17Client';
import CustomReportManageDate from './CustomReportManageDate';

import './styles.scss';
import { QUESTION } from '../../constants/terminology';

interface CardProps {
  title: string;
  image: string;
  description: JSX.Element;
  onClick: () => void;
  handleDownloadExample: () => void;
  disabled?: boolean;
}

const Card = (props: CardProps) => {
  if (props.disabled) {
    return null;
  }

  return (
    <div>
      {props.title}
      <div onClick={props.onClick} className='custom-report-disambiguation-card text-center'>
        <div className='p-2'>
          <img src={props.image} alt={props.title} />
        </div>
        <Button color='primary' onClick={props.onClick}>
          Build report
        </Button>
      </div>
      <div className='mt-1'>
        <div className='text-right mb-2'>
          <Button color='link' onClick={props.handleDownloadExample}>
            Download example
          </Button>
        </div>
        <p className='mb-0'>{props.description}</p>
      </div>
    </div>
  );
};

interface CustomReportCreateDisambiguationProps {
  handleReload: () => Promise<void>;
}

const CustomReportCreateDisambiguation = (props: CustomReportCreateDisambiguationProps) => {
  const { params } = useRouteMatch<{ createType?: string }>();
  const history = useHistory();
  const initiativeState = useAppSelector((state) => state.initiative);

  if (!initiativeState.loaded) {
    return <Loader />;
  }

  const initiativeId = initiativeState.id;

  const handleGoToCreate = (createType: string) => () => {
    history.push(generateUrl(CustomReportRoutes.CREATE_REPORT, { initiativeId, createType }));
  };

  const handleDownloadExample = async (type: string) => {
    return G17Client.customReportDownloadExample(initiativeId, type).catch((e) => console.log(e));
  };

  switch (params.createType) {
    case 'calculated':
      return <CustomReportManage {...props} />;
    case 'subsidiary':
      return <CustomReportManageSubsidiary {...props} />;
    case 'date':
      return <CustomReportManageDate {...props} />;
  }

  return (
    <Dashboard className='custom-report-disambiguation'>
      <DashboardSection title='Custom report builder'>
        <h4>
          <b>What type of report do you want to build:</b>
        </h4>
        <div className='row mt-3'>
          <div className='col-4'>
            <Card
              title='Calculated'
              image={CalculatedReportImage}
              onClick={handleGoToCreate('calculated')}
              handleDownloadExample={() => handleDownloadExample('calculated')}
              description={
                <span>
                  Selecting this reporting type will allow you to build your own report containing only the metrics you
                  want to review or report against.
                  <br />
                  <br />
                  Calculated {QUESTION.PLURAL} allow you to select and use specific inputs from a {QUESTION.SINGULAR} to create your own
                  outputs, such as number of female employees divided by total employee count.
                </span>
              }
            />
          </div>
          <div className='col-4'>
            <Card
              title='Subsidiary comparison'
              image={SubsidiaryReportImage}
              onClick={handleGoToCreate('subsidiary')}
              handleDownloadExample={() => handleDownloadExample('subsidiary')}
              description={
                <span>
                  Selecting this reporting type will allow you to review and compare how your subsidiaries have answered
                  their survey {QUESTION.PLURAL} for a specified month. All in scope {QUESTION.PLURAL} are included when this report is
                  downloaded.
                  <br />
                  <br />A subsidiary is any region, office, or sub-company that exists within your Company Tracker
                  organisational tree and is able to answer {QUESTION.PLURAL}.
                </span>
              }
            />
          </div>
          <div className='col-4'>
            <Card
              title='Date comparison'
              image={DateReportImage}
              onClick={handleGoToCreate('date')}
              handleDownloadExample={() => handleDownloadExample('date')}
              description={
                <span>
                  Selecting this reporting type will allow you to compare and validate how your organisation has
                  answered their survey’s {QUESTION.PLURAL} over a specified timeframe, for example January to December. All in
                  scope {QUESTION.PLURAL} are included when this report is downloaded.
                  <br />
                  <br />
                  You will only be able to select survey dates from this organisation level.
                </span>
              }
            />
          </div>
        </div>
      </DashboardSection>
    </Dashboard>
  );
};

export default CustomReportCreateDisambiguation;
